// The caller: who they are, which organisation they are acting in, and how the
// transport gets both.
//
// The credential itself lives in lib/auth.ts, which signs in against the
// app's own accounts; this holds the organisation beside it and re-renders
// when either changes.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { configureClient } from '../api/client';
import { getTokens, logout, subscribe } from './auth';

interface Session {
  /** Whether this browser holds a credential: a token, or a test subject. */
  signedIn: boolean;
  /** The organisation every request acts in. */
  orgId: string | null;
  setOrgId: (id: string | null) => void;
  signOut: () => void;
}

const Ctx = createContext<Session | null>(null);

const ORG_KEY = 'inbox.org';

// At import, so the transport is pointed at the API before any screen's first
// query — a child's effect runs before its provider's. The provider swaps in
// its own handlers once it mounts.
configureClient(
  () => {},
  () => {},
);

function readOrg(): string | null {
  try {
    return localStorage.getItem(ORG_KEY);
  } catch {
    return null;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const tokens = useSyncExternalStore(subscribe, getTokens);
  const [orgId, setOrgIdState] = useState<string | null>(readOrg);
  const qc = useQueryClient();

  const setOrgId = useCallback((id: string | null) => {
    setOrgIdState(id);
    try {
      if (id) localStorage.setItem(ORG_KEY, id);
      else localStorage.removeItem(ORG_KEY);
    } catch {
      /* the choice lasts this session */
    }
  }, []);

  const signOut = useCallback(() => {
    setOrgId(null);
    qc.clear();
    void logout();
  }, [setOrgId, qc]);

  // A renewed token means whatever was refused can be asked for again; no
  // credential left means the sign-in page, and nothing cached from the
  // account that was signed in.
  useEffect(() => {
    configureClient(
      () => void qc.invalidateQueries(),
      () => {
        setOrgId(null);
        qc.clear();
      },
    );
  }, [qc, setOrgId]);

  const value = useMemo(
    () => ({ signedIn: !!tokens, orgId, setOrgId, signOut }),
    [tokens, orgId, setOrgId, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession outside SessionProvider');
  return s;
}
